import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";

import { App } from "./App";
import { startThemeSync } from "./lib/theme";
import { stopIosFocusZoom } from "./lib/ios-zoom";
import { AwayProvider } from "./lib/away";
import { NotificationsProvider } from "./lib/notifications";
import { SocketProvider } from "./lib/socket";
import "./index.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /*
       * Long stale time on purpose. The socket pushes changes as they happen,
       * so aggressive refetching would duplicate work the realtime layer has
       * already done. Refetch-on-focus stays enabled as the safety net for a
       * tab that was asleep and missed events while disconnected.
       */
      staleTime: 60_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        // Never retry an auth failure -- it will not resolve itself, and
        // retrying just delays the redirect to the login screen.
        if (error instanceof Error && "status" in error && error.status === 401) {
          return false;
        }
        return failureCount < 2;
      },
    },
  },
});

const root = document.getElementById("root");
if (!root) throw new Error("#root element is missing from index.html");

/*
 * Before the first paint, so a dark-mode user never sees a white flash -- and
 * it keeps listening afterwards, so choosing a theme in the CRM tab repaints
 * the Helpdesk tab sitting beside it. The preference is the person's, not the
 * product's.
 */
startThemeSync();

/* Before anybody can tap a field -- see `ios-zoom.ts`. */
stopIosFocusZoom();

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <SocketProvider>
          {/* Inside SocketProvider: the feed is built from socket events, and
              inside the router, because a notification navigates. */}
          <NotificationsProvider>
            {/* Outside nothing in particular, but inside the query client:
                every SLA countdown reads this, so it has to be above the
                screens rather than beside them. */}
            <AwayProvider>
              <App />
            </AwayProvider>
          </NotificationsProvider>
        </SocketProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
